import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Seo, ORG_SCHEMA, WEBSITE_SCHEMA } from "@/components/Seo";
import { toast } from "sonner";
import {
  ArrowUpRight, ArrowRight, ShieldCheck, FileText, Layers, CheckCircle2,
  Building2, ClipboardCheck, Lock, Flame, Wallet, Wrench, Users, AlertTriangle,
  Sparkles, Gift,
} from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1643142314913-0cf633d9bbb5?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const VALUE = "https://images.unsplash.com/photo-1552652893-2aa10a0ab4df?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const CAT_ICONS = {
  "safety-osha": ShieldCheck, "hr-job-descriptions": Users, "service-procedures": Wrench,
  "parts-procedures": Layers, "body-shop": Building2, "security-access": Lock,
  "cash-handling": Wallet, "emergency-response": AlertTriangle, "onboarding-training": ClipboardCheck,
  "forms-checklists": FileText,
};

export default function Home() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [recent, setRecent] = useState([]);
  const [stats, setStats] = useState(null);
  const [email, setEmail] = useState("");
  const [heroQuery, setHeroQuery] = useState("");

  const runFinder = () => {
    const q = heroQuery.trim();
    if (q.length < 3) return toast.error("Tell us a little about the situation.");
    navigate("/forms", { state: { finderQuery: q } });
  };

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/forms/recent", { params: { limit: 8 } }).then((r) => setRecent(r.data)).catch(() => {});
    api.get("/stats/public").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const submitLead = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      await api.post("/leads", { email, source: "homepage" });
      toast.success("You're on the list. Check your inbox for the free sample pack.");
      setEmail("");
    } catch {
      toast.error("Could not submit. Try again.");
    }
  };

  return (
    <div>
      <Seo
        title="Centennial Dealership Operating System | SOPs, Forms & Training"
        description="Operating procedures, management forms, compliance resources and training for dealership service, parts, sales, HR and collision. One system. Every department. Every time."
        canonicalPath="/"
        jsonLd={[ORG_SCHEMA, WEBSITE_SCHEMA]}
      />
      {/* HERO */}
      <section className="relative">
        <div className="absolute inset-0">
          <img src={HERO} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/55 to-black/25" />
        </div>
        <div className="relative max-w-[1280px] mx-auto px-6 lg:px-8 py-28 lg:py-40">
          <div className="max-w-2xl fade-up">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/80 border border-white/20 px-3 py-1.5 mb-6">
              <span className="w-1.5 h-1.5 bg-[#3B82F6]" /> Centennial Dealership Operating System
            </span>
            <h1 className="font-heading font-black text-white text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.02]">
              Run a safer, more consistent dealership — without building every SOP from scratch.
            </h1>
            <p className="text-white/80 text-lg mt-6 leading-relaxed max-w-xl">
              Protect the dealership, train your people, and document every action — with {stats ? `${stats.documents}+` : "289+"} ready-to-use
              SOPs and forms built on a simple <span className="text-white font-semibold">Process → Procedure → Reference</span> system.
              Not sure where to start? Describe the situation below and we'll point you to the exact forms.
            </p>

            {/* AI FORM FINDER — hero invite */}
            <div className="mt-8 bg-white/10 backdrop-blur-md border border-white/25 p-4 rounded-sm" data-testid="hero-finder">
              <div className="flex items-center gap-2 text-white text-sm font-semibold mb-2.5">
                <Sparkles className="w-4 h-4 text-[#93C5FD]" />
                Describe what happened — our Form Finder does the rest
              </div>
              <div className="flex flex-col sm:flex-row gap-2.5">
                <input
                  value={heroQuery}
                  onChange={(e) => setHeroQuery(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && runFinder()}
                  placeholder="e.g. a tech backed a customer's car into a pole"
                  data-testid="hero-finder-input"
                  className="flex-1 bg-white text-black px-4 py-3.5 focus:outline-none focus:ring-2 focus:ring-[#3B82F6] placeholder:text-black/40"
                />
                <button onClick={runFinder} data-testid="hero-finder-submit"
                  className="inline-flex items-center justify-center gap-2 bg-[#002FA7] text-white px-6 py-3.5 font-semibold btn-lift whitespace-nowrap">
                  <Sparkles className="w-4 h-4" /> Find the forms I need
                </button>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-x-6 gap-y-3 mt-6">
              <Link to="/forms" data-testid="hero-cta-secondary"
                className="inline-flex items-center gap-2 text-white font-semibold hover:gap-3 transition-all">
                Browse the full library <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/forms" data-testid="hero-cta-free"
                className="inline-flex items-center gap-2 text-[#93C5FD] font-semibold hover:gap-3 transition-all">
                <Gift className="w-4 h-4" /> Or claim any 3 forms free
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* STAT BAR */}
      <section className="border-b border-border bg-white">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-8 grid grid-cols-2 md:grid-cols-4">
          {[
            [stats ? `${stats.documents}+` : "289+", "Documents"],
            [stats ? String(stats.departments) : "6", "Departments"],
            ["3", "System tiers"],
            ["100%", "Editable & branded"],
          ].map(([n, l], i) => (
            <div key={i} className="py-8 px-2 border-r last:border-r-0 border-border">
              <div className="font-heading font-black text-3xl lg:text-4xl tracking-tight text-primary">{n}</div>
              <div className="text-sm text-muted-foreground mt-1">{l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* PROBLEM / VALUE */}
      <section className="max-w-[1280px] mx-auto px-6 lg:px-8 py-24">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <span className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">The problem</span>
            <h2 className="font-heading font-bold text-3xl lg:text-4xl tracking-tight mt-3 leading-tight">
              Your processes live in people's heads. That's a liability.
            </h2>
            <p className="text-muted-foreground mt-5 leading-relaxed">
              When a manager leaves, the playbook leaves with them. Compliance gaps, inconsistent training,
              and "we've always done it this way" cost real money and real risk.
            </p>
            <ul className="mt-7 space-y-4">
              {[
                "Standardize every department with proven SOPs",
                "Stay audit-ready for OSHA & safety compliance",
                "Onboard new hires in days, not months",
                "Protect cash, keys, and customer vehicles",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                  <span className="text-foreground/90">{t}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="relative">
            <img src={VALUE} alt="Reviewing dealership operations" className="w-full h-[460px] object-cover" />
            <div className="absolute -bottom-6 -left-6 bg-primary text-white p-6 max-w-[220px] hidden sm:block">
              <div className="font-heading font-black text-2xl">Decades</div>
              <div className="text-sm text-white/80 mt-1">of fixed-ops & dealership experience, systemized.</div>
            </div>
          </div>
        </div>
      </section>

      {/* PRODUCT LADDER */}
      <section className="bg-secondary border-y border-border">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-8 py-24">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-12">
            <div>
              <span className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">Choose your level</span>
              <h2 className="font-heading font-bold text-3xl lg:text-4xl tracking-tight mt-3">Three ways to deploy</h2>
            </div>
            <Link to="/products" className="text-sm font-semibold text-primary inline-flex items-center gap-1">
              Compare all products <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {products.map((p) => (
              <Link key={p.id} to={`/products/${p.slug}`} data-testid={`home-product-${p.id}`}
                className={`group bg-white border p-7 flex flex-col btn-lift ${p.tier_level === 2 ? "border-primary border-2" : "border-border"}`}>
                {p.badge && (
                  <span className={`self-start text-[11px] uppercase tracking-wider font-semibold px-2 py-1 mb-4 ${p.tier_level === 2 ? "bg-primary text-white" : "bg-secondary text-muted-foreground"}`}>
                    {p.badge}
                  </span>
                )}
                <h3 className="font-heading font-bold text-xl tracking-tight">{p.short_name}</h3>
                <p className="text-sm text-muted-foreground mt-2 flex-1">{p.tagline}</p>
                <div className="mt-6 flex items-end justify-between">
                  <div className="font-heading font-black text-3xl">${p.price.toLocaleString()}</div>
                  <span className="text-primary group-hover:translate-x-1 transition-transform"><ArrowUpRight className="w-5 h-5" /></span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="max-w-[1280px] mx-auto px-6 lg:px-8 py-24">
        <span className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">What's inside</span>
        <h2 className="font-heading font-bold text-3xl lg:text-4xl tracking-tight mt-3 mb-12">Every department, documented</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 border-l border-t border-border">
          {categories.map((c) => {
            const Icon = CAT_ICONS[c.slug] || FileText;
            return (
              <div key={c.slug} data-testid={`home-category-${c.slug}`} className="border-r border-b border-border p-6 hover:bg-secondary transition-colors">
                <Icon className="w-6 h-6 text-primary mb-4" />
                <div className="font-semibold text-sm leading-tight">{c.name}</div>
                <div className="text-xs text-muted-foreground mt-1 font-mono">{c.count} docs</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* RECENTLY UPDATED */}
      {recent.length > 0 && (
        <section className="border-t border-border bg-white" data-testid="home-recent">
          <div className="max-w-[1280px] mx-auto px-6 lg:px-8 py-20">
            <div className="flex items-end justify-between gap-4 mb-10">
              <div>
                <span className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">Freshly maintained</span>
                <h2 className="font-heading font-bold text-3xl lg:text-4xl tracking-tight mt-3">New &amp; recently updated</h2>
              </div>
              <Link to="/forms" data-testid="home-recent-all"
                className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:gap-2.5 transition-all whitespace-nowrap">
                Browse all forms <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {recent.map((f) => (
                <Link key={f.id} to={`/forms/${f.code}`} data-testid={`home-recent-${f.code}`}
                  className="group border border-border bg-white p-5 hover:border-primary transition-colors flex flex-col">
                  <div className="flex items-center justify-between">
                    <div className="w-9 h-9 bg-secondary flex items-center justify-center">
                      <FileText className="w-4 h-4 text-primary" />
                    </div>
                    <ArrowUpRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
                  </div>
                  <h3 className="font-semibold text-sm leading-snug mt-4 line-clamp-2">{f.title}</h3>
                  <div className="mt-auto pt-3 flex items-center gap-2">
                    <span className="font-mono text-[10px] bg-secondary px-1.5 py-0.5 text-foreground/70">{f.code}</span>
                    {(f.updated_at || f.uploaded_at) && (
                      <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                        {new Date(f.updated_at || f.uploaded_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                      </span>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* PACK / FREE PROMO */}
      <section className="border-y border-border bg-secondary">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-8 py-20">
          <div className="max-w-2xl">
            <span className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">Start small — grow when you're ready</span>
            <h2 className="font-heading font-bold text-3xl lg:text-4xl tracking-tight mt-3 leading-tight">
              Build to your needs. No big commitment to begin.
            </h2>
            <p className="text-muted-foreground mt-4">
              Start with 3 forms free, add a pack when you need a few more, and step up to a full system only when it's
              worth it for your store. You're always in control of the pace.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-10" data-testid="home-ladder">
            {[
              { step: "Step 1", name: "Any 3 forms", price: "Free", note: "New here? Pick 3 on us — no card needed.", to: "/forms", cta: "Claim 3 free", highlight: true, icon: Gift },
              { step: "Step 2", name: "3-Pack", price: "$39", note: "Grab exactly the 3 forms you keep reaching for.", to: "/forms", cta: "Build a 3-pack", icon: Layers },
              { step: "Step 3", name: "5-Pack", price: "$59", note: "A few more? Bundle any 5 and save.", to: "/forms", cta: "Build a 5-pack", icon: Layers },
              { step: "When ready", name: "Full Systems", price: "from $99", note: "Whole departments, done. Toolkit → Library → CDOS.", to: "/products", cta: "See the systems", icon: ShieldCheck },
            ].map((t) => (
              <div key={t.name}
                className={`bg-white border p-6 flex flex-col ${t.highlight ? "border-primary ring-1 ring-primary" : "border-border"}`}
                data-testid={`ladder-${t.name.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="flex items-center justify-between">
                  <span className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">{t.step}</span>
                  <t.icon className={`w-4 h-4 ${t.highlight ? "text-primary" : "text-muted-foreground"}`} />
                </div>
                <div className="font-heading font-bold text-lg mt-3">{t.name}</div>
                <div className={`font-heading font-black text-2xl mt-1 ${t.highlight ? "text-primary" : "text-foreground"}`}>{t.price}</div>
                <p className="text-sm text-muted-foreground mt-2 flex-1">{t.note}</p>
                <Link to={t.to} data-testid={`ladder-cta-${t.name.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`inline-flex items-center gap-1.5 text-sm font-semibold mt-4 ${t.highlight ? "text-primary" : "text-foreground hover:text-primary"} transition-colors`}>
                  {t.cta} <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LEAD MAGNET */}
      <section className="bg-[#0A0A0A] text-white">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-8 py-20 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="font-heading font-bold text-3xl lg:text-4xl tracking-tight leading-tight">
              Get 3 essential dealership forms — free
            </h2>
            <p className="text-white/60 mt-4">No cost. Sample the quality before you buy. Straight to your inbox.</p>
          </div>
          <form onSubmit={submitLead} className="flex flex-col sm:flex-row gap-3" data-testid="home-lead-form">
            <input
              type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="you@dealership.com" data-testid="home-lead-email"
              className="flex-1 bg-white/5 border border-white/15 px-4 py-3.5 text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <button type="submit" data-testid="home-lead-submit"
              className="bg-white text-black px-6 py-3.5 font-semibold btn-lift whitespace-nowrap">
              Send me the pack
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
